import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  MenuItem,
  Typography,
} from "@mui/material";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { Branch } from "./Branch";
import { commonButtonProps, commonTextFieldProps } from "../../../utils/styles";

interface AddBranchModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (branch: any) => void;
  branch?: Branch;
  isEdit?: boolean;
}

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .required("Branch name is required")
    .min(2, "Name must be at least 2 characters")
    .max(100, "Name must not exceed 100 characters"),
  contactPerson: Yup.string()
    .required("Contact Person is required")
    .min(2, "Contact Person must be at least 2 characters")
    .max(50, "Contact Person must not exceed 50 characters"),
  contactNumber: Yup.string()
    .required("Contact number is required")
    .matches(/^[0-9]+$/, "Contact number must contain only digits")
    .min(10, "Contact number must be exactly 10 digits")
    .max(10, "Contact number must be exactly 10 digits"),
  email: Yup.string().nullable().email("Invalid email format"),
  address: Yup.string()
    .nullable()
    .max(200, "Address must not exceed 200 characters"),
  commissionType: Yup.string().optional(),
  commissionValue: Yup.string().when("commissionType", {
    is: (val: string) => val && val.length > 0,
    then: (schema) =>
      schema.required(
        "Commission value is required when commission type is selected"
      ),
    otherwise: (schema) => schema.optional(),
  }),
});

const initialValues: Omit<Branch, "id"> = {
  name: "",
  contactPerson: "",
  contactNumber: "",
  email: "",
  address: "",
  companyId: "",
  commissionType: "",
  commissionValue: "",
};

const AddBranchModal: React.FC<AddBranchModalProps> = ({
  open,
  onClose,
  onSubmit,
  branch,
  isEdit = false,
}) => {
  const localUser = sessionStorage.getItem("user");
  const companyId = localUser ? JSON.parse(localUser).companyId : null;

  const handleSubmit = (values: any) => {
    const enrichedValues = {
      ...values,
      ...(!isEdit && { companyId: companyId }),
    };
    onSubmit(enrichedValues);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? "Update Branch" : "Add New Branch"}</DialogTitle>
      <Formik
        initialValues={branch || initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, errors, touched, isValid, dirty }) => (
          <Form>
            <DialogContent sx={{ p: 2, pt: 0 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ fontSize: "0.9rem" }}>
                    Branch Information
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Branch Name"
                    name="name"
                    value={values.name}
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                    required
                    {...commonTextFieldProps}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Email (optional)"
                    name="email"
                    type="email"
                    value={values.email}
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email && errors.email}
                    placeholder="Enter email address"
                    {...commonTextFieldProps}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ fontSize: "0.9rem" }}>
                    Commission Information
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Field
                    as={TextField}
                    select
                    fullWidth
                    label="Pricing Type"
                    name="commissionType"
                    value={values.commissionType}
                    error={touched.commissionType && !!errors.commissionType}
                    helperText={touched.commissionType && errors.commissionType}
                    // onChange={handleChange}
                    {...commonTextFieldProps}
                  >
                    {["Percentage", "Fixed"].map((type) => (
                      <MenuItem
                        key={type}
                        value={type}
                        sx={{ fontSize: "0.75rem" }}
                      >
                        {type}
                      </MenuItem>
                    ))}
                  </Field>
                </Grid>

                <Grid item xs={12} sm={3}>
                  <Field
                    as={TextField}
                    fullWidth
                    label={
                      values.commissionType === "Fixed"
                        ? "Commission per item"
                        : "Commission percentage"
                    }
                    name="commissionValue"
                    type="number"
                    value={values.commissionValue}
                    error={touched.commissionValue && !!errors.commissionValue}
                    helperText={
                      touched.commissionValue && errors.commissionValue
                    }
                    // onChange={handleChange}
                    InputProps={{
                      startAdornment:
                        values.commissionType === "Fixed" ? (
                          <span style={{ marginRight: 8, fontSize: "0.75rem" }}>
                            ₹
                          </span>
                        ) : null,
                      endAdornment:
                        values.commissionType === "Percentage" ? (
                          <span style={{ marginLeft: 8, fontSize: "0.75rem" }}>
                            %
                          </span>
                        ) : null,
                      inputProps: {
                        style: { textAlign: "left" },
                        onWheel: (e: React.WheelEvent<HTMLInputElement>) =>
                          (e.target as HTMLInputElement).blur(),
                      },
                    }}
                    {...commonTextFieldProps}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ fontSize: "0.9rem" }}>
                    Contact Information
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Contact Person"
                    name="contactPerson"
                    value={values.contactPerson}
                    error={
                      touched.contactPerson && Boolean(errors.contactPerson)
                    }
                    helperText={touched.contactPerson && errors.contactPerson}
                    required
                    {...commonTextFieldProps}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Contact Number"
                    name="contactNumber"
                    value={values.contactNumber}
                    error={
                      touched.contactNumber && Boolean(errors.contactNumber)
                    }
                    helperText={touched.contactNumber && errors.contactNumber}
                    inputProps={{
                      maxLength: 10,
                    }}
                    InputProps={{
                      inputMode: "numeric",
                    }}
                    placeholder="Enter 10 digit number"
                    required
                    {...commonTextFieldProps}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Address"
                    name="address"
                    multiline
                    rows={2}
                    error={touched.address && Boolean(errors.address)}
                    helperText={touched.address && errors.address}
                    placeholder="Enter address"
                    value={values.address}
                    {...commonTextFieldProps}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={onClose} {...commonButtonProps}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={!(isValid && dirty)}
                {...commonButtonProps}
              >
                {isEdit ? "Update" : "Add"}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default AddBranchModal;
